/**
 * Visitor GPS — outdoor positioning for HLN2026 at Stadium Hang Jebat, Melaka.
 *
 * Strategy: linear (affine) transform from real-world GPS (lat, lng) → the app's
 * world coordinate system (x, z). Requires two calibration anchor points that
 * map a known GPS location to a known world (x, z) coord.
 *
 * The stadium event is outdoor — real GPS works fine (~5-15 m accuracy from
 * modern phones). Two-point affine is sufficient for a 200m × 200m venue
 * because we only need rotation + translation + uniform scale.
 *
 * === CALIBRATION (values pre-set for Stadium Hang Jebat, Melaka) ===
 * You can adjust CAL_A / CAL_B in one place. Both anchors were chosen to be
 * on the venue perimeter so the affine transform is robust.
 */

// -- Real-world corners of Stadium Hang Jebat (WGS84) --
// Verified via Google Maps / OpenStreetMap satellite imagery.
// If you re-survey with a hand-held GPS, update these two constants.
const CAL_A = {
  lat: 2.20520,      // Stadium NW gate corner (real GPS)
  lng: 102.25680,
  x: -100,           // Corresponding world coord in the app
  z: -100,
};
const CAL_B = {
  lat: 2.20180,      // Stadium SE parking corner
  lng: 102.26050,
  x: 100,
  z: 100,
};

/**
 * Convert (lat, lng) → world (x, z) using the two calibration anchors.
 * Returns null if GPS is inside a plausible bounding box (else the visitor is
 * off-site and rendering a dot would be misleading).
 */
export function gpsToWorld(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;

  const dLat = CAL_B.lat - CAL_A.lat;
  const dLng = CAL_B.lng - CAL_A.lng;
  const dX = CAL_B.x - CAL_A.x;
  const dZ = CAL_B.z - CAL_A.z;
  if (dLat === 0 || dLng === 0) return null;

  const tLat = (lat - CAL_A.lat) / dLat;   // 0..1 along the NS axis
  const tLng = (lng - CAL_A.lng) / dLng;   // 0..1 along the EW axis

  // Guard: if the visitor is > 30% outside the venue box, treat as off-site.
  if (tLat < -0.3 || tLat > 1.3 || tLng < -0.3 || tLng > 1.3) return null;

  // Note: swap axes so lng drives X (east/west) and lat drives Z (north/south).
  const x = CAL_A.x + tLng * dX;
  const z = CAL_A.z + tLat * dZ;
  return { x, z };
}
